import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  RefreshCw, ArrowLeft, CheckCircle, XCircle, Clock, AlertTriangle,
  Building2, Loader2, ChevronDown, ChevronRight,
} from 'lucide-react';

const STATUS_MAP = {
  queued: { label: 'Queued', variant: 'secondary', icon: Clock },
  running: { label: 'Running', variant: 'secondary', icon: Loader2 },
  completed: { label: 'Completed', variant: 'default', icon: CheckCircle },
  completed_with_errors: { label: 'Partial', variant: 'warning', icon: AlertTriangle },
  failed: { label: 'Failed', variant: 'destructive', icon: XCircle },
};

function formatDuration(startedAt, completedAt) {
  if (!startedAt) return '-';
  const start = new Date(startedAt);
  const end = completedAt ? new Date(completedAt) : new Date();
  const s = (end - start) / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

export default function SyncJobDetail() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchJob = async () => {
    try {
      const [jobRes, logsRes] = await Promise.all([
        api.get(`/sync-jobs/${jobId}`),
        api.get(`/sync-jobs/${jobId}/logs`),
      ]);
      setJob(jobRes.data);
      setLogs(logsRes.data.logs || []);
    } catch (err) {
      console.error('Failed to load job', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJob();
    // Poll if running
    const interval = setInterval(() => {
      fetchJob();
    }, 5000);
    return () => clearInterval(interval);
  }, [jobId]);

  // Stop polling when completed
  useEffect(() => {
    if (job && !['queued', 'running'].includes(job.status)) {
      // No need to poll anymore
    }
  }, [job]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-background p-8">
        <p className="text-muted-foreground">Job not found: {jobId}</p>
        <Link to="/super-admin" className="text-blue-600 hover:underline text-sm mt-2 block">Back to Admin</Link>
      </div>
    );
  }

  const st = STATUS_MAP[job.status] || STATUS_MAP.queued;
  const StIcon = st.icon;

  return (
    <div className="min-h-screen bg-background" data-testid="sync-job-detail">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/super-admin" className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-lg font-bold">Sync Job Detail</h1>
                <p className="text-xs text-muted-foreground font-mono">{jobId}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" className="rounded-none" onClick={fetchJob}>
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Job Summary Card */}
        <div className="border border-border bg-card p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Status</p>
              <div className="flex items-center gap-2">
                <StIcon className={`h-4 w-4 ${job.status === 'running' ? 'animate-spin' : ''} ${job.status === 'completed' ? 'text-emerald-600' : job.status === 'failed' ? 'text-red-600' : 'text-blue-600'}`} />
                <span className="font-semibold">{st.label}</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Type</p>
              <span className={`text-xs px-2 py-0.5 rounded-none font-medium ${job.type === 'ubm_sync' ? 'bg-indigo-100 text-indigo-700' : 'bg-cyan-100 text-cyan-700'}`}>
                {job.type === 'ubm_sync' ? 'UBM Sync' : 'Payment Sync'}
              </span>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Scope</p>
              <span className="font-medium text-sm">{job.scope === 'customer' ? job.customer_name || 'Single Org' : 'Full Sync'}</span>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Duration</p>
              <span className="font-mono text-sm">{formatDuration(job.started_at, job.completed_at)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-4 pt-4 border-t border-border">
            <div>
              <p className="text-xs text-muted-foreground">Created</p>
              <p className="text-xs font-mono">{job.created_at ? new Date(job.created_at).toLocaleString() : '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Started</p>
              <p className="text-xs font-mono">{job.started_at ? new Date(job.started_at).toLocaleString() : '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Completed</p>
              <p className="text-xs font-mono">{job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Triggered By</p>
              <p className="text-xs font-medium">{job.triggered_by}</p>
            </div>
          </div>

          {/* Progress */}
          {job.progress && job.progress.total > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Progress: {job.progress.completed + job.progress.failed}/{job.progress.total} customers</span>
                {job.progress.current && <span className="text-blue-600">Currently: {job.progress.current}</span>}
              </div>
              <div className="w-full bg-muted h-2 overflow-hidden">
                <div
                  className="bg-primary h-2 transition-all duration-300"
                  style={{ width: `${Math.round(((job.progress.completed + job.progress.failed) / job.progress.total) * 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Error */}
          {job.error && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs text-red-600 font-medium mb-1">Error</p>
              <pre className="text-xs bg-red-50 text-red-800 p-3 overflow-x-auto border border-red-200">{job.error}</pre>
            </div>
          )}

          {/* Totals */}
          {job.totals && Object.keys(job.totals).length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Result Summary</p>
              <div className="flex flex-wrap gap-4">
                {Object.entries(job.totals).map(([k, v]) => (
                  <div key={k} className="bg-muted/30 px-3 py-1.5 border border-border">
                    <span className="text-xs text-muted-foreground">{k.replace(/_/g, ' ')}: </span>
                    <span className="text-sm font-mono font-medium">{typeof v === 'number' ? v.toLocaleString() : String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Customer Logs */}
        <div className="border border-border bg-card">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold">Customer Sync Logs ({logs.length})</h3>
          </div>
          <div className="divide-y divide-border">
            {logs.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-sm">No customer logs recorded</div>
            ) : (
              logs.map(log => {
                const isExpanded = expandedLog === log.log_id;
                return (
                  <div key={log.log_id} data-testid={`log-${log.log_id}`}>
                    <button
                      className="w-full flex items-center justify-between p-3 hover:bg-muted/20 text-left"
                      onClick={() => setExpandedLog(isExpanded ? null : log.log_id)}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">{log.customer_name}</span>
                        {log.status === 'completed' ? (
                          <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5 text-red-600" />
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{log.duration_ms ? `${(log.duration_ms / 1000).toFixed(1)}s` : '-'}</span>
                        <span>{log.errors?.length > 0 ? `${log.errors.length} errors` : 'No errors'}</span>
                        <span>{new Date(log.created_at).toLocaleString()}</span>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-10 pb-4 space-y-3">
                        {/* Details */}
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Details</p>
                            <pre className="text-xs bg-muted/30 p-3 border border-border overflow-x-auto max-h-60">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </div>
                        )}
                        {/* Errors */}
                        {log.errors && log.errors.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-red-600 mb-1">Errors</p>
                            {log.errors.map((err, i) => (
                              <pre key={i} className="text-xs bg-red-50 text-red-800 p-2 border border-red-200 mb-1">{err}</pre>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
